import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions } from 'react-query'
import { api } from 'src/api'
import { Config } from 'src/types'

export type TDataTerminalConfiguration = Config

export type TParamsUseTerminalConfiguration = {
  terminalId?: string
}

export type TParamsPostTerminalConfiguration = {
  terminalId?: string
  config: Config
}

export type TQueryOptionsUseTerminalConfiguration = UseQueryOptions<
  TDataTerminalConfiguration,
  Error,
  TDataTerminalConfiguration,
  ReturnType<typeof getQueryKeyTerminalConfiguration>
>

export type TQueryOptionsUseMutationTerminalConfiguration = UseMutationOptions<
  TDataTerminalConfiguration,
  Error,
  TParamsPostTerminalConfiguration,
  unknown
>

export const fetchTerminalConfiguration: (
  params: TParamsUseTerminalConfiguration
) => Promise<TDataTerminalConfiguration> = ({ terminalId }) => {
  return api.get(`/v2/configuration/terminal?terminalId=${terminalId}`)
}

export const postTerminalConfiguration: (
  params: TParamsPostTerminalConfiguration
) => Promise<TDataTerminalConfiguration> = ({ terminalId, config }) => {
  return api.put(`/v2/configuration/terminal?terminalId=${terminalId}`, config)
}

export const getQueryKeyTerminalConfiguration = ({ terminalId }: TParamsUseTerminalConfiguration) =>
  ['/v2/configuration/terminal', { terminalId }] as const

export function useQueryTerminalConfiguration(
  params: TParamsUseTerminalConfiguration,
  options?: TQueryOptionsUseTerminalConfiguration
) {
  const { terminalId } = params
  return useQuery({
    queryKey: getQueryKeyTerminalConfiguration(params),
    enabled: Boolean(terminalId),
    queryFn: async () => {
      if (!terminalId) throw new Error('Missing terminalId')
      return fetchTerminalConfiguration(params)
    },
    ...options
  })
}

export function useMutationTerminalConfiguration(options?: TQueryOptionsUseMutationTerminalConfiguration) {
  const queryClient = useQueryClient()
  return useMutation<TDataTerminalConfiguration, Error, TParamsPostTerminalConfiguration>({
    mutationFn: postTerminalConfiguration,
    onSuccess: (_data: TDataTerminalConfiguration, variables: TParamsPostTerminalConfiguration) => {
      queryClient.invalidateQueries(getQueryKeyTerminalConfiguration({ terminalId: variables.terminalId }))
    },
    ...options
  })
}

export default function useTerminalConfiguration(
  params: TParamsUseTerminalConfiguration,
  options?: TQueryOptionsUseTerminalConfiguration
) {
  const queryHook = useQueryTerminalConfiguration(params, options)
  const mutationHook = useMutationTerminalConfiguration()

  // Provide shaped output similar to previous hook: { data, loading, error }
  return {
    ...queryHook,
    mutation: mutationHook
  }
}
